import { onDestroy } from "@/modules/_";
import { ScrollTrigger } from "@/lib/gsap";
import { Scroll } from "@/lib/scroll";

const CACHE_PREFIX = "ressources-preview:";

const SELECTORS = {
  grid: ".ressources-preview_grid",
  item: '[data-element="item"]',
  nextPage: '[data-element="next-wf-page"]',
  loadMore: '[data-element="load-more"]',
  topicList: ".topic-filter_list",
  topicPill: ".topic-filter_pill",
  topicInput: ".topic-filter_pill_input",
  topicLabel: ".topic-filter_pill_label",
};

/**
 * Ressources-Preview Component
 * - Fetches all paginated pages and merges items into one grid
 * - Builds topic filter from data-topic attributes (alphabetically)
 * - Load-more shows initial batch size each click; disables when exhausted
 * - Topic filter filters items by data-topic
 * - Uses View Transitions API for show/hide (see CSS instructions below)
 */
export default function (element, dataset) {
  const grid = element.querySelector(SELECTORS.grid);
  const topicList = element.querySelector(SELECTORS.topicList);
  const loadMoreBtn = element.querySelector(SELECTORS.loadMore);
  const firstPill = element.querySelector(SELECTORS.topicPill);

  if (!grid || !topicList || !firstPill) {
    console.warn("ressources-preview: Missing required elements (grid, topic list, or pill template)");
    return;
  }

  let allItems = [];
  let initialVisibleCount = 0;
  let visibleCount = 0;
  let selectedTopic = "all";

  const supportsViewTransition = typeof document.startViewTransition === "function";

  function refreshScrollSystems() {
    requestAnimationFrame(() => {
      ScrollTrigger.refresh();
      if (Scroll && typeof Scroll.resize === "function") {
        Scroll.resize();
      }
    });
  }

  function transitionUpdate(callback) {
    if (supportsViewTransition) {
      return document.startViewTransition(callback);
    }
    callback();
    return { finished: Promise.resolve(), ready: Promise.resolve() };
  }

  function getItemTopics(itemEl) {
    const topicEls = itemEl.querySelectorAll("[data-topic]");
    return Array.from(topicEls).map((el) => el.getAttribute("data-topic")).filter(Boolean);
  }

  function slugify(text) {
    return String(text)
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
  }

  async function fetchAllPages() {
    const baseUrl = window.location.origin + window.location.pathname;

    // Use current DOM as first page (no duplicate fetch)
    const initialItems = Array.from(grid.querySelectorAll(SELECTORS.item));
    initialItems.forEach((el) => allItems.push(el));
    initialVisibleCount = allItems.length;
    visibleCount = initialVisibleCount;

    let nextLink = element.querySelector(SELECTORS.nextPage);
    let url = nextLink ? nextLink.getAttribute("href") : null;
    const seenUrls = new Set();

    while (url) {
      const normalized = url.startsWith("http") ? url : baseUrl + (url.startsWith("?") ? url : "?" + url);
      if (seenUrls.has(normalized)) break;
      seenUrls.add(normalized);

      try {
        let html = null;
        try {
          html = sessionStorage.getItem(CACHE_PREFIX + normalized);
        } catch (_) {}

        if (!html) {
          const res = await fetch(normalized, { headers: { Accept: "text/html" } });
          if (!res.ok) break;
          html = await res.text();
          try {
            sessionStorage.setItem(CACHE_PREFIX + normalized, html);
          } catch (_) {}
        }

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const moduleEl = doc.querySelector('[data-module="ressources-preview"]');
        const pageGrid = moduleEl?.querySelector(SELECTORS.grid);
        const items = pageGrid ? Array.from(pageGrid.querySelectorAll(SELECTORS.item)) : [];

        for (const itemEl of items) {
          const clone = itemEl.cloneNode(true);
          grid.appendChild(clone);
          allItems.push(clone);
          const index = allItems.length - 1;
          if (index >= initialVisibleCount) {
            clone.hidden = true;
            clone.setAttribute("aria-hidden", "true");
          }
        }

        nextLink = moduleEl?.querySelector(SELECTORS.nextPage);
        url = nextLink ? nextLink.getAttribute("href") || null : null;
      } catch (e) {
        console.warn("ressources-preview: Failed to fetch page", url, e);
        break;
      }
    }

    if (allItems.length > 0) {
      allItems.forEach((item, i) => {
        item.dataset.ressourcesPreviewIndex = String(i);
      });
      grid.style.viewTransitionName = "ressources-preview-grid";
    }
  }

  function updateItemVisibility(useTransition = true) {
    const update = () => {
      const matchingIndices =
        selectedTopic === "all"
          ? null
          : allItems
              .map((item, index) => (getItemTopics(item).includes(selectedTopic) ? index : -1))
              .filter((i) => i >= 0);

      allItems.forEach((item, index) => {
        const show =
          selectedTopic === "all"
            ? index < visibleCount
            : matchingIndices.indexOf(index) >= 0 && matchingIndices.indexOf(index) < visibleCount;
        item.hidden = !show;
        item.setAttribute("aria-hidden", show ? "false" : "true");
      });

      if (loadMoreBtn) {
        const hasMore =
          selectedTopic === "all"
            ? visibleCount < allItems.length
            : visibleCount < matchingIndices.length;
        loadMoreBtn.disabled = !hasMore;
        loadMoreBtn.setAttribute("aria-disabled", loadMoreBtn.disabled ? "true" : "false");
      }
      refreshScrollSystems();
    };

    if (useTransition && supportsViewTransition) {
      transitionUpdate(update);
    } else {
      update();
    }
  }

  function buildTopicPills() {
    const topicSet = new Set();
    allItems.forEach((item) => getItemTopics(item).forEach((t) => topicSet.add(t)));
    const sorted = Array.from(topicSet).sort((a, b) => a.localeCompare(b, "de"));

    sorted.forEach((topic) => {
      const id = `topic-${slugify(topic)}`;
      const pill = document.createElement("div");
      pill.className = firstPill.className;
      pill.innerHTML = `
        <input type="radio" name="topic" id="${id}" class="topic-filter_pill_input" value="${escapeHtml(topic)}">
        <label for="${id}" class="topic-filter_pill_label">${escapeHtml(topic)}</label>
      `;
      topicList.appendChild(pill);

      const input = pill.querySelector("input");
      input.addEventListener("change", () => {
        selectedTopic = topic;
        updateItemVisibility(true);
      });
    });
  }

  function escapeHtml(str) {
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  }

  function initLoadMore() {
    if (!loadMoreBtn) return;
    loadMoreBtn.addEventListener("click", () => {
      if (loadMoreBtn.disabled) return;
      visibleCount = Math.min(visibleCount + initialVisibleCount, allItems.length);
      updateItemVisibility(true);
    });
  }

  function initTopicFilter() {
    const allInput = element.querySelector(`input[name="topic"][value="all"]`);
    if (allInput) {
      allInput.addEventListener("change", () => {
        selectedTopic = "all";
        updateItemVisibility(true);
      });
    }
  }

  function hidePaginationAndExtraUi() {
    const pagination = element.querySelector(".w-pagination-wrapper");
    if (pagination) pagination.classList.add("hide");
  }

  (async () => {
    await fetchAllPages();
    updateItemVisibility(false);
    buildTopicPills();
    initLoadMore();
    initTopicFilter();
    hidePaginationAndExtraUi();
  })();

  element.setAttribute("data-initialized", "true");

  onDestroy(() => {
    element.removeAttribute("data-initialized");
    if (grid) grid.style.viewTransitionName = "";
    allItems.forEach((item) => delete item.dataset.ressourcesPreviewIndex);
  });
}

/*
  View Transitions API – CSS für ressources-preview
  In dein Projekt-CSS einfügen (z.B. app.css oder Global Custom Code in Webflow):

  .ressources-preview_component .ressources-preview_grid {
    view-transition-name: ressources-preview-grid;
  }

  @media (prefers-reduced-motion: no-preference) {
    ::view-transition-old(ressources-preview-grid) {
      animation: ressources-preview-fade-out 0.2s ease-out both;
    }
    ::view-transition-new(ressources-preview-grid) {
      animation: ressources-preview-fade-in 0.25s ease-out both;
    }
  }

  @keyframes ressources-preview-fade-out {
    to { opacity: 0; }
  }
  @keyframes ressources-preview-fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  Optional: Einblenden der Items etwas verzögern (stagger), wenn du pro Item
  view-transition-name setzt (z.B. ressources-preview-item-0, -1, …), dann:

  [data-ressources-preview-index] {
    view-transition-name: attr(data-ressources-preview-index) ressources-preview-item;
  }
  (attr() für view-transition-name wird noch nicht überall unterstützt – dann
  das Grid-Transition oben verwenden.)
*/
